import { useState, useEffect } from 'react';
import Header from '@/components/Header';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEnhancedUserMetrics } from '@/hooks/useEnhancedUserMetrics';
import { useAchievements } from '@/hooks/useAchievements';
import { useUserVehicles } from '@/hooks/useUserVehicles';
import { useRuns } from '@/hooks/useRuns';
import { useUserTrailPreferences } from '@/hooks/useTrailPreferences';
import { ProfileHeader } from '@/components/ProfileHeader';
import { MetricCard } from '@/components/MetricCard';
import { ProfileGarage } from '@/components/ProfileGarage';
import { ProfileActivity } from '@/components/ProfileActivity';
import { ProfileAchievements } from '@/components/ProfileAchievements';
import { TrailCompletionDialog } from '@/components/TrailCompletionDialog';
import { RunsListDialog } from '@/components/RunsListDialog';
import { FavoriteTrailsDialog } from '@/components/FavoriteTrailsDialog';
import { WantToGoDialog } from '@/components/WantToGoDialog';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Mountain, 
  Gauge, 
  Calendar, 
  Clock, 
  TrendingUp, 
  Camera, 
  MapPin,
  Timer,
  Heart,
  Target
} from "lucide-react";
import { ROUTES } from '@/lib/routes';

interface UserProfile {
  id: string;
  username?: string;
  bio?: string;
  location?: string;
  avatar_url?: string;
  banner_url?: string;
}

const Profile = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [trailDialogOpen, setTrailDialogOpen] = useState(false);
  const [runsDialogOpen, setRunsDialogOpen] = useState(false);
  const [favoritesDialogOpen, setFavoritesDialogOpen] = useState(false);
  const [wantToGoDialogOpen, setWantToGoDialogOpen] = useState(false);

  const isOwnProfile = user?.id === userId;
  
  // Use trail preferences only for own profile (due to RLS)
  const { favoritesCount, wantToGoCount, isLoading: isLoadingPreferences } = useUserTrailPreferences();

  // Fetch user profile data
  const fetchProfile = async () => {
    if (!userId) return;

    setIsLoadingProfile(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles_public')
        .select('id, username, bio, location, avatar_url, banner_url')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  // Fetch metrics, achievements, and vehicles
  const { data: metrics, isLoading: isLoadingMetrics } = useEnhancedUserMetrics(userId);
  const { earnedAchievements, availableAchievements, isLoading: isLoadingAchievements } = useAchievements(userId);
  const { runs } = useRuns(userId, 'start_time');
  const { 
    vehicles, 
    isLoading: isLoadingVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    setFeaturedVehicle,
  } = useUserVehicles(userId);

  const handleTrailsClick = () => {
    setTrailDialogOpen(true);
  };

  const handleSeeRuns = () => {
    setRunsDialogOpen(true);
  };

  const handleLogRun = () => {
    navigate(ROUTES.logRun);
  };

  const handleSeeLastRun = () => {
    if (metrics?.lastRun) {
      navigate(ROUTES.run(metrics.lastRun.id));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Profile Header */}
          {isLoadingProfile ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ProfileHeader
              userId={userId || ''}
              username={profile?.username}
              bio={profile?.bio}
              location={profile?.location}
              avatarUrl={profile?.avatar_url}
              bannerUrl={profile?.banner_url}
              isOwnProfile={isOwnProfile}
              onUpdate={fetchProfile}
            />
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
            <MetricCard
              title="Trails Completed"
              value={metrics?.trailsCompletedCount || 0}
              icon={Mountain}
              gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
              subtitle={
                metrics?.trailsCompletedCount
                  ? "≥80% of segments completed"
                  : "Complete your first trail"
              }
              actions={
                metrics && metrics.trailsCompletedCount > 0
                  ? [
                      {
                        label: "See Trails",
                        onClick: handleTrailsClick,
                        variant: "outline",
                      },
                    ]
                  : undefined
              }
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Miles Aired Down"
              value={metrics?.totalMiles || 0}
              icon={Gauge}
              gradient="bg-gradient-to-br from-blue-500 to-blue-600"
              subtitle="Total distance traveled"
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Hours Aired Down"
              value={metrics?.totalHours || 0}
              icon={Timer}
              gradient="bg-gradient-to-br from-amber-500 to-amber-600"
              subtitle="Time spent on trails"
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Runs Logged"
              value={metrics?.totalRuns || 0}
              icon={Calendar}
              gradient="bg-gradient-to-br from-purple-500 to-purple-600"
              subtitle={
                metrics?.totalRuns
                  ? "Adventures tracked"
                  : "Log your first run"
              }
              actions={[
                ...(metrics && metrics.totalRuns > 0
                  ? [
                      {
                        label: "See Runs",
                        onClick: handleSeeRuns,
                        variant: "outline" as const,
                      },
                    ]
                  : []),
                ...(isOwnProfile
                  ? [
                      {
                        label: "Log Run",
                        onClick: handleLogRun,
                        variant: "outline" as const,
                      },
                    ]
                  : []),
              ]}
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Last Run"
              value={metrics?.lastRun?.relativeTime || "Never"}
              icon={Clock}
              gradient="bg-gradient-to-br from-orange-500 to-orange-600"
              subtitle={metrics?.lastRun?.title || "No runs logged yet"}
              actions={
                metrics?.lastRun
                  ? [
                      {
                        label: "See Run",
                        onClick: handleSeeLastRun,
                        variant: "outline",
                      },
                    ]
                  : undefined
              }
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Active Months This Year"
              value={
                metrics
                  ? `${metrics.activeMonthsThisYear.count}/${metrics.activeMonthsThisYear.total}`
                  : "0/0"
              }
              icon={TrendingUp}
              gradient="bg-gradient-to-br from-pink-500 to-pink-600"
              subtitle={
                metrics?.activeMonthsThisYear.hasStreak
                  ? `${metrics.activeMonthsThisYear.streakLength} month streak!`
                  : "Keep the momentum going"
              }
              badge={
                metrics?.activeMonthsThisYear.hasStreak
                  ? {
                      text: `🔥 ${metrics.activeMonthsThisYear.streakLength} month streak!`,
                      variant: "secondary",
                    }
                  : undefined
              }
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Photos Shared"
              value={metrics?.photosShared || 0}
              icon={Camera}
              gradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
              subtitle={
                metrics?.photosShared
                  ? "Memories captured"
                  : "Share your adventures"
              }
              isLoading={isLoadingMetrics}
            />

            <MetricCard
              title="Waypoints Shared"
              value={metrics?.waypointsShared || 0}
              icon={MapPin}
              gradient="bg-gradient-to-br from-teal-500 to-teal-600"
              subtitle={
                metrics?.waypointsShared
                  ? "Points of interest marked"
                  : "Mark your favorite spots"
              }
              isLoading={isLoadingMetrics}
            />

            {isOwnProfile && (
              <>
                <MetricCard
                  title="Favorite Trails"
                  value={favoritesCount}
                  icon={Heart}
                  gradient="bg-gradient-to-br from-red-500 to-red-600"
                  subtitle={
                    favoritesCount > 0
                      ? "Trails you love"
                      : "Save your favorite trails"
                  }
                  actions={
                    favoritesCount > 0
                      ? [
                          {
                            label: "View",
                            onClick: () => setFavoritesDialogOpen(true),
                            variant: "outline",
                          },
                        ]
                      : undefined
                  }
                  isLoading={isLoadingPreferences}
                />

                <MetricCard
                  title="Want to Go"
                  value={wantToGoCount}
                  icon={Target}
                  gradient="bg-gradient-to-br from-cyan-500 to-cyan-600"
                  subtitle={
                    wantToGoCount > 0
                      ? "Your trail wishlist"
                      : "Build your adventure list"
                  }
                  actions={
                    wantToGoCount > 0
                      ? [
                          {
                            label: "View",
                            onClick: () => setWantToGoDialogOpen(true),
                            variant: "outline",
                          },
                        ]
                      : undefined
                  }
                  isLoading={isLoadingPreferences}
                />
              </>
            )}
          </div>

          {/* Garage - with edit capabilities if own profile */}
          <ProfileGarage 
            vehicles={vehicles} 
            isLoading={isLoadingVehicles}
            isOwnProfile={isOwnProfile}
            userId={userId}
            onUpdate={updateVehicle}
            onDelete={deleteVehicle}
            onSetFeatured={setFeaturedVehicle}
            onCreate={createVehicle}
          />

          {/* Achievements */}
          <ProfileAchievements
            earnedAchievements={earnedAchievements}
            availableAchievements={availableAchievements}
            isLoading={isLoadingAchievements}
            userMetrics={metrics}
          />

          {/* Activity */}
          <ProfileActivity recentRuns={runs?.slice(0, 5)} isLoading={isLoadingMetrics} />
        </div>
      </main>

      {/* Dialogs */}
      <TrailCompletionDialog 
        open={trailDialogOpen} 
        onOpenChange={setTrailDialogOpen}
        trails={metrics?.trailsCompleted || []}
      />
      <RunsListDialog
        open={runsDialogOpen}
        onOpenChange={setRunsDialogOpen}
        runs={runs}
      />
      <FavoriteTrailsDialog
        open={favoritesDialogOpen}
        onOpenChange={setFavoritesDialogOpen}
      />
      <WantToGoDialog
        open={wantToGoDialogOpen}
        onOpenChange={setWantToGoDialogOpen}
      />
    </div>
  );
};

export default Profile;
